import {
  MOCK_TEST_GROUP_IDS,
  TEST_ENV_NUMBERS,
  CENT_MOCK_LINE,
  CONVENIENT_MD_AUTO_ATTENDANT_LINE_BY_INBOUND_LINE,
  CONVENIENT_MD_LINES,
  CONVENIENT_MD_PILOT_SITE_LINES,
  CONVENIENT_MD_QUINCY_LINE,
  CONVENIENT_MD_TEST_LINE,
  CTNC_EXISTING_PATIENT_LINE,
  CTNC_EXISTING_PATIENT_TRANSFER_LINE,
  CTNC_NEW_PATIENT_LINE,
  CTNC_NEW_PATIENT_TRANSFER_LINE,
  ENTP_LINE,
  ENTP_MOCK_LINE,
  ENTP_SCI_LINE,
  ENTP_SCI_MOCK_LINE,
  ENTP_SCI_TRANSFER_LINE,
  ENTP_TRANSFER_LINE_MEDIUM_PRIORITY,
  GroupIds,
  NIMBUS_MACON_LINE,
  NIMBUS_MACON_MOCK_LINE,
  NIMBUS_ROCK_HILL_LINE,
  NIMBUS_ROCK_HILL_MOCK_LINE,
  NIMBUS_SCW_LINE,
  NIMBUS_SCW_MOCK_LINE,
  PMG_CLINIC_LINE,
  PMG_ERICA_SEND_DIGITS,
  PMG_LIVE_TWILIO_NUMBER,
  PMG_SANDBOX_TWILIO_NUMBER,
  SAMPLE_ALLOW_LIST,
  AXIA_BLUE_BELL_LINE,
  AXIA_DEMO_LINE,
  AXIA_LANSDALE_LINE,
  AXIA_OGI_NORTH_LINE,
  AXIA_POTTSTOWN_LINE,
  AXIA_TRANSFER_LINE,
  SPORT_ORTHO_DEMO_LINE,
  SPORT_ORTHO_TRANSFER_LINE,
  SPORT_ORTHO_TRANSFER_SEND_DIGITS,
  TPR_BILTMORE_AFTER_HOURS_LINE,
  TPR_BILTMORE_LEGACY_IVR_LINE,
  TPR_BILTMORE_VOICE_LINE,
  TPR_BILTMORE_VOICE_MOCK_LINE,
  UPPERLINE_GA_LINE,
  UPPERLINE_GA_MOCK_LINE,
  UPPERLINE_GA_TRANSFER_LINE,
  VOICE_STAGING_TWILIO_NUMBER,
} from "@/shared/group-configs/groups";

export type VoiceOpenHours = {
  openMinuteOfDay: number;
  closeMinuteOfDay: number;
};

export type VoiceAfterHoursRoutingConfig = {
  forwardingNumber: string;
  timeZone: string;
  openHoursByWeekday: Readonly<Partial<Record<number, VoiceOpenHours>>>;
};

export type VoiceInboundRoutingConfig = {
  groupId: GroupIds;
  /** Classifies ingress alert scope separately from agent test-session policy. */
  isProductionTraffic?: boolean;
  forwardingNumber: string | null;
  forwardingSendDigits?: string;
  afterHoursRouting?: VoiceAfterHoursRoutingConfig;
  voiceAgentSipUri: string;
  voiceAgentSampleRate: number;
  samplingAllowList: readonly string[];
  alertTag: string;
  runEligibilityCheck: boolean;
};

export type VoiceInboundProductionTrafficByGroup = Readonly<Partial<Record<GroupIds, boolean>>>;

export enum InboundVoiceRoute {
  AfterHours = "after_hours",
  Clinic = "clinic",
  Ended = "ended",
  OperatorBypass = "operator_bypass",
  VoiceAgent = "voice_agent",
}

export function isTestVoiceTraffic(routing: VoiceInboundRoutingConfig | null, calledNumber: string): boolean {
  return routing?.isProductionTraffic !== undefined
    ? !routing.isProductionTraffic
    : TEST_ENV_NUMBERS.includes(calledNumber) || (routing !== null && MOCK_TEST_GROUP_IDS.includes(routing.groupId));
}

function convenientMdVoiceInboundRouting(
  groupId: GroupIds,
  lines: readonly (typeof CONVENIENT_MD_LINES)[number][],
  voiceAgentSampleRate: number,
  alertTag: string
): Readonly<Record<string, VoiceInboundRoutingConfig>> {
  return Object.fromEntries(
    lines.map((line) => [
      line,
      {
        groupId,
        forwardingNumber: CONVENIENT_MD_AUTO_ATTENDANT_LINE_BY_INBOUND_LINE[line],
        voiceAgentSipUri: `sip:${line}@3pgrb3k9tuv.sip.livekit.cloud`,
        voiceAgentSampleRate,
        samplingAllowList: SAMPLE_ALLOW_LIST,
        runEligibilityCheck: false,
        alertTag,
      },
    ])
  );
}

export const voiceInboundRoutingByNumber: Readonly<Record<string, VoiceInboundRoutingConfig>> = {
  [PMG_LIVE_TWILIO_NUMBER]: {
    groupId: GroupIds.PMG_LIVE,
    forwardingNumber: PMG_CLINIC_LINE,
    forwardingSendDigits: PMG_ERICA_SEND_DIGITS,
    voiceAgentSipUri: `sip:${PMG_LIVE_TWILIO_NUMBER}@3pgrb3k9tuv.sip.livekit.cloud`,
    voiceAgentSampleRate: 1.0,
    samplingAllowList: SAMPLE_ALLOW_LIST,
    runEligibilityCheck: false,
    alertTag: "PMG - Prod",
  },
  [PMG_SANDBOX_TWILIO_NUMBER]: {
    groupId: GroupIds.PMG_LIVE_SANDBOX,
    forwardingNumber: null,
    voiceAgentSipUri: `sip:${PMG_SANDBOX_TWILIO_NUMBER}@3pgrb3k9tuv.sip.livekit.cloud`,
    voiceAgentSampleRate: 1.0,
    samplingAllowList: SAMPLE_ALLOW_LIST,
    runEligibilityCheck: false,
    alertTag: "PMG Sandbox",
  },
  [CTNC_NEW_PATIENT_LINE]: {
    groupId: GroupIds.NIRA_LIVE,
    forwardingNumber: CTNC_NEW_PATIENT_TRANSFER_LINE,
    voiceAgentSipUri: `sip:${CTNC_NEW_PATIENT_LINE}@3pgrb3k9tuv.sip.livekit.cloud`,
    voiceAgentSampleRate: 0.0,
    samplingAllowList: SAMPLE_ALLOW_LIST,
    alertTag: "New Patient Line",
    runEligibilityCheck: true,
  },
  [CTNC_EXISTING_PATIENT_LINE]: {
    groupId: GroupIds.NIRA_LIVE,
    forwardingNumber: CTNC_EXISTING_PATIENT_TRANSFER_LINE,
    voiceAgentSipUri: `sip:${CTNC_EXISTING_PATIENT_LINE}@3pgrb3k9tuv.sip.livekit.cloud`,
    voiceAgentSampleRate: 0.0,
    samplingAllowList: SAMPLE_ALLOW_LIST,
    alertTag: "Existing Patient Line",
    runEligibilityCheck: true,
  },
  [UPPERLINE_GA_LINE]: {
    groupId: GroupIds.UPPERLINE_GEORGIA_LIVE,
    forwardingNumber: UPPERLINE_GA_TRANSFER_LINE,
    voiceAgentSipUri: `sip:${UPPERLINE_GA_LINE}@3pgrb3k9tuv.sip.livekit.cloud`,
    voiceAgentSampleRate: 1.0,
    samplingAllowList: SAMPLE_ALLOW_LIST,
    alertTag: "Upperline GA",
    runEligibilityCheck: true,
  },
  [ENTP_LINE]: {
    groupId: GroupIds.ENT_PARTNERS_LIVE,
    forwardingNumber: ENTP_TRANSFER_LINE_MEDIUM_PRIORITY,
    voiceAgentSipUri: `sip:${ENTP_LINE}@3pgrb3k9tuv.sip.livekit.cloud`,
    voiceAgentSampleRate: 1.0,
    samplingAllowList: SAMPLE_ALLOW_LIST,
    alertTag: "ENT Partners",
    runEligibilityCheck: true,
  },
  [ENTP_SCI_LINE]: {
    groupId: GroupIds.ENT_PARTNERS_SCI_LIVE,
    forwardingNumber: ENTP_SCI_TRANSFER_LINE,
    voiceAgentSipUri: `sip:${ENTP_SCI_LINE}@3pgrb3k9tuv.sip.livekit.cloud`,
    voiceAgentSampleRate: 1.0,
    samplingAllowList: SAMPLE_ALLOW_LIST,
    alertTag: "ENT Partners SCI",
    runEligibilityCheck: true,
  },
  [NIMBUS_SCW_LINE]: {
    groupId: GroupIds.NIMBUS_LIVE,
    forwardingNumber: null,
    voiceAgentSipUri: `sip:${NIMBUS_SCW_LINE}@3pgrb3k9tuv.sip.livekit.cloud`,
    voiceAgentSampleRate: 1.0,
    samplingAllowList: SAMPLE_ALLOW_LIST,
    runEligibilityCheck: true,
    alertTag: "Nimbus Sun City West",
  },
  [NIMBUS_ROCK_HILL_LINE]: {
    groupId: GroupIds.NIMBUS_LIVE,
    forwardingNumber: null,
    voiceAgentSipUri: `sip:${NIMBUS_ROCK_HILL_LINE}@3pgrb3k9tuv.sip.livekit.cloud`,
    voiceAgentSampleRate: 1.0,
    samplingAllowList: SAMPLE_ALLOW_LIST,
    runEligibilityCheck: true,
    alertTag: "Nimbus Rock Hill",
  },
  [NIMBUS_MACON_LINE]: {
    groupId: GroupIds.NIMBUS_LIVE,
    forwardingNumber: null,
    voiceAgentSipUri: `sip:${NIMBUS_MACON_LINE}@3pgrb3k9tuv.sip.livekit.cloud`,
    voiceAgentSampleRate: 1.0,
    samplingAllowList: SAMPLE_ALLOW_LIST,
    runEligibilityCheck: true,
    alertTag: "Nimbus Macon",
  },
  [VOICE_STAGING_TWILIO_NUMBER]: {
    groupId: GroupIds.VALERIE_HEALTH_LIVE_SANDBOX,
    forwardingNumber: VOICE_STAGING_TWILIO_NUMBER,
    voiceAgentSipUri: `sip:${VOICE_STAGING_TWILIO_NUMBER}@3pgrb3k9tuv.sip.livekit.cloud`,
    voiceAgentSampleRate: 1.0,
    samplingAllowList: SAMPLE_ALLOW_LIST,
    runEligibilityCheck: false,
    alertTag: "Voice Staging",
  },
  ...convenientMdVoiceInboundRouting(
    GroupIds.CONVENIENT_MD_LIVE_SANDBOX,
    [CONVENIENT_MD_TEST_LINE],
    1.0,
    "ConvenientMD Sandbox"
  ),
  ...convenientMdVoiceInboundRouting(
    GroupIds.CONVENIENT_MD_LIVE,
    CONVENIENT_MD_PILOT_SITE_LINES.filter((line) => line !== CONVENIENT_MD_QUINCY_LINE),
    0.0,
    "ConvenientMD"
  ),
  ...convenientMdVoiceInboundRouting(GroupIds.CONVENIENT_MD_LIVE, [CONVENIENT_MD_QUINCY_LINE], 0.5, "ConvenientMD"),
  [SPORT_ORTHO_DEMO_LINE]: {
    groupId: GroupIds.SPORTORTHO_LIVE,
    forwardingNumber: SPORT_ORTHO_TRANSFER_LINE,
    forwardingSendDigits: SPORT_ORTHO_TRANSFER_SEND_DIGITS,
    voiceAgentSipUri: `sip:${SPORT_ORTHO_DEMO_LINE}@3pgrb3k9tuv.sip.livekit.cloud`,
    voiceAgentSampleRate: 0.5,
    samplingAllowList: SAMPLE_ALLOW_LIST,
    // Left off deliberately: the pre-warm only shaves latency, and Sport Ortho builds its
    // scheduling context in-call after identity, as TPR does.
    runEligibilityCheck: false,
    alertTag: "Sport Ortho",
  },
  [AXIA_DEMO_LINE]: {
    groupId: GroupIds.VALERIE_HEALTH_LIVE_SANDBOX,
    forwardingNumber: AXIA_TRANSFER_LINE,
    voiceAgentSipUri: `sip:${AXIA_DEMO_LINE}@3pgrb3k9tuv.sip.livekit.cloud`,
    voiceAgentSampleRate: 1.0,
    samplingAllowList: SAMPLE_ALLOW_LIST,
    runEligibilityCheck: false,
    alertTag: "Axia Demo",
  },
  [AXIA_OGI_NORTH_LINE]: {
    groupId: GroupIds.AXIA_LIVE,
    // No fallback to the clinic while this is a test line; Axia's own forwarding comes later.
    forwardingNumber: null,
    voiceAgentSipUri: `sip:${AXIA_OGI_NORTH_LINE}@3pgrb3k9tuv.sip.livekit.cloud`,
    voiceAgentSampleRate: 1.0,
    samplingAllowList: SAMPLE_ALLOW_LIST,
    runEligibilityCheck: false,
    alertTag: "Axia OGI North",
  },
  [AXIA_BLUE_BELL_LINE]: {
    groupId: GroupIds.AXIA_LIVE,
    // No fallback to the clinic while this is a test line; Axia's own forwarding comes later.
    forwardingNumber: null,
    voiceAgentSipUri: `sip:${AXIA_BLUE_BELL_LINE}@3pgrb3k9tuv.sip.livekit.cloud`,
    voiceAgentSampleRate: 1.0,
    samplingAllowList: SAMPLE_ALLOW_LIST,
    runEligibilityCheck: false,
    alertTag: "Axia Blue Bell",
  },
  [AXIA_LANSDALE_LINE]: {
    groupId: GroupIds.AXIA_LIVE,
    // No fallback to the clinic while this is a test line; Axia's own forwarding comes later.
    forwardingNumber: null,
    voiceAgentSipUri: `sip:${AXIA_LANSDALE_LINE}@3pgrb3k9tuv.sip.livekit.cloud`,
    voiceAgentSampleRate: 1.0,
    samplingAllowList: SAMPLE_ALLOW_LIST,
    runEligibilityCheck: false,
    alertTag: "Axia Lansdale",
  },
  [AXIA_POTTSTOWN_LINE]: {
    groupId: GroupIds.AXIA_LIVE,
    // No fallback to the clinic while this is a test line; Axia's own forwarding comes later.
    forwardingNumber: null,
    voiceAgentSipUri: `sip:${AXIA_POTTSTOWN_LINE}@3pgrb3k9tuv.sip.livekit.cloud`,
    voiceAgentSampleRate: 1.0,
    samplingAllowList: SAMPLE_ALLOW_LIST,
    runEligibilityCheck: false,
    alertTag: "Axia Pottstown",
  },
  [ENTP_SCI_MOCK_LINE]: {
    groupId: GroupIds.ENT_PARTNERS_SCI_LIVE_SANDBOX,
    forwardingNumber: ENTP_SCI_TRANSFER_LINE,
    voiceAgentSipUri: `sip:${ENTP_SCI_MOCK_LINE}@3pgrb3k9tuv.sip.livekit.cloud`,
    voiceAgentSampleRate: 1.0,
    samplingAllowList: SAMPLE_ALLOW_LIST,
    runEligibilityCheck: false,
    alertTag: "SCENT Mock",
  },
  [ENTP_MOCK_LINE]: {
    groupId: GroupIds.ENT_PARTNERS_LIVE_SANDBOX,
    forwardingNumber: ENTP_TRANSFER_LINE_MEDIUM_PRIORITY,
    voiceAgentSipUri: `sip:${ENTP_MOCK_LINE}@3pgrb3k9tuv.sip.livekit.cloud`,
    voiceAgentSampleRate: 1.0,
    samplingAllowList: SAMPLE_ALLOW_LIST,
    runEligibilityCheck: false,
    alertTag: "ENTP Mock",
  },
  [CENT_MOCK_LINE]: {
    groupId: GroupIds.ENT_PARTNERS_LIVE_SANDBOX,
    forwardingNumber: ENTP_TRANSFER_LINE_MEDIUM_PRIORITY,
    voiceAgentSipUri: `sip:${CENT_MOCK_LINE}@3pgrb3k9tuv.sip.livekit.cloud`,
    voiceAgentSampleRate: 1.0,
    samplingAllowList: SAMPLE_ALLOW_LIST,
    runEligibilityCheck: false,
    alertTag: "CENT Mock",
  },
  [UPPERLINE_GA_MOCK_LINE]: {
    groupId: GroupIds.UPPERLINE_GEORGIA_LIVE_SANDBOX,
    forwardingNumber: UPPERLINE_GA_TRANSFER_LINE,
    voiceAgentSipUri: `sip:${UPPERLINE_GA_MOCK_LINE}@3pgrb3k9tuv.sip.livekit.cloud`,
    voiceAgentSampleRate: 1.0,
    samplingAllowList: SAMPLE_ALLOW_LIST,
    runEligibilityCheck: false,
    alertTag: "Upperline GA Mock",
  },
  [NIMBUS_SCW_MOCK_LINE]: {
    groupId: GroupIds.NIMBUS_LIVE_SANDBOX,
    forwardingNumber: null,
    voiceAgentSipUri: `sip:${NIMBUS_SCW_MOCK_LINE}@3pgrb3k9tuv.sip.livekit.cloud`,
    voiceAgentSampleRate: 1.0,
    samplingAllowList: SAMPLE_ALLOW_LIST,
    runEligibilityCheck: false,
    alertTag: "Nimbus Sun City West Mock",
  },
  [NIMBUS_ROCK_HILL_MOCK_LINE]: {
    groupId: GroupIds.NIMBUS_LIVE_SANDBOX,
    forwardingNumber: null,
    voiceAgentSipUri: `sip:${NIMBUS_ROCK_HILL_MOCK_LINE}@3pgrb3k9tuv.sip.livekit.cloud`,
    voiceAgentSampleRate: 1.0,
    samplingAllowList: SAMPLE_ALLOW_LIST,
    runEligibilityCheck: false,
    alertTag: "Nimbus Rock Hill Mock",
  },
  [NIMBUS_MACON_MOCK_LINE]: {
    groupId: GroupIds.NIMBUS_LIVE_SANDBOX,
    forwardingNumber: null,
    voiceAgentSipUri: `sip:${NIMBUS_MACON_MOCK_LINE}@3pgrb3k9tuv.sip.livekit.cloud`,
    voiceAgentSampleRate: 1.0,
    samplingAllowList: SAMPLE_ALLOW_LIST,
    runEligibilityCheck: false,
    alertTag: "Nimbus Macon Mock",
  },
  [TPR_BILTMORE_VOICE_LINE]: {
    groupId: GroupIds.TPR_BILTMORE_LIVE,
    forwardingNumber: TPR_BILTMORE_LEGACY_IVR_LINE,
    afterHoursRouting: {
      forwardingNumber: TPR_BILTMORE_AFTER_HOURS_LINE,
      timeZone: "America/Phoenix",
      openHoursByWeekday: {
        1: { openMinuteOfDay: 7 * 60, closeMinuteOfDay: 17 * 60 },
        2: { openMinuteOfDay: 7 * 60, closeMinuteOfDay: 17 * 60 },
        3: { openMinuteOfDay: 7 * 60, closeMinuteOfDay: 17 * 60 },
        4: { openMinuteOfDay: 7 * 60, closeMinuteOfDay: 17 * 60 },
        5: { openMinuteOfDay: 7 * 60, closeMinuteOfDay: 15 * 60 },
      },
    },
    voiceAgentSipUri: `sip:${TPR_BILTMORE_VOICE_LINE}@3pgrb3k9tuv.sip.livekit.cloud`,
    voiceAgentSampleRate: 1.0,
    samplingAllowList: SAMPLE_ALLOW_LIST,
    // TPR resolves eligibility after identity and scheduling context are established in-call.
    runEligibilityCheck: false,
    alertTag: "TPR Biltmore",
  },
  [TPR_BILTMORE_VOICE_MOCK_LINE]: {
    groupId: GroupIds.TPR_BILTMORE_LIVE_SANDBOX,
    forwardingNumber: null,
    voiceAgentSipUri: `sip:${TPR_BILTMORE_VOICE_MOCK_LINE}@3pgrb3k9tuv.sip.livekit.cloud`,
    voiceAgentSampleRate: 1.0,
    samplingAllowList: SAMPLE_ALLOW_LIST,
    runEligibilityCheck: false,
    alertTag: "TPR Biltmore Mock",
  },
};

export function getVoiceInboundRoutingConfig(
  calledNumber: string,
  productionTrafficByGroup: VoiceInboundProductionTrafficByGroup = {}
): VoiceInboundRoutingConfig | null {
  const routing = voiceInboundRoutingByNumber[calledNumber];

  if (!routing) return null;

  const isProductionTraffic = productionTrafficByGroup[routing.groupId];

  return isProductionTraffic === undefined ? routing : { ...routing, isProductionTraffic };
}

const isoWeekdayByShortName: Readonly<Record<string, number>> = {
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
  Sun: 7,
};

/** The ISO weekday (1 = Monday) and minutes after local midnight for an instant in a time zone. */
export function getLocalWeekdayAndMinuteOfDay(
  instant: Date,
  timeZone: string
): { weekday: number; minuteOfDay: number } {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(instant);
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  const weekday = isoWeekdayByShortName[values.weekday ?? ""];
  const hour = Number(values.hour);
  const minute = Number(values.minute);

  if (!weekday || !Number.isInteger(hour) || !Number.isInteger(minute)) {
    throw new Error(`Unable to resolve local time in ${timeZone}`);
  }

  return { weekday, minuteOfDay: hour * 60 + minute };
}

export function getAfterHoursForwardingNumber(
  config: VoiceInboundRoutingConfig,
  instant: Date = new Date()
): string | null {
  const afterHoursRouting = config.afterHoursRouting;

  if (!afterHoursRouting) return null;

  const { weekday, minuteOfDay } = getLocalWeekdayAndMinuteOfDay(instant, afterHoursRouting.timeZone);
  const openHours = afterHoursRouting.openHoursByWeekday[weekday];
  const isOpen =
    openHours !== undefined && minuteOfDay >= openHours.openMinuteOfDay && minuteOfDay < openHours.closeMinuteOfDay;

  return isOpen ? null : afterHoursRouting.forwardingNumber;
}

export function shouldRouteCallToVoiceAgent(
  config: VoiceInboundRoutingConfig,
  fromNumber: string,
  randomValue: number = Math.random()
): boolean {
  return config.samplingAllowList.includes(fromNumber) || randomValue < config.voiceAgentSampleRate;
}
